import { StarIcon } from '@heroicons/react/20/solid';
import React, { useEffect, useState } from 'react';
import { TTMenuItem } from '../../../interfaces/menuItem';
import { SubscriptionProductPrices } from '../../../interfaces/subscription';
import { centsToDollars } from '../../utils';
import { CustomAmountInput } from './CustomAmountInput';

type Props = {
  menuItem: TTMenuItem;
  onSelection: (
    price_ids: number[],
    price_data: { [key: string]: any },
  ) => void;
};

export function SubscriptionInfo({ menuItem, onSelection }: Props) {
  const [selectedPrice, setSelectedPrice] = useState<
    SubscriptionProductPrices[]
  >([]);
  const [requestCustomAmount, setRequestCustomAmount] =
    useState<SubscriptionProductPrices | null>(null);
  const [customPriceData, setCustomPriceData] = useState<{
    [key: string]: any;
  }>({});

  useEffect(() => {
    if (
      menuItem &&
      menuItem?.subscription_product?.prices?.filter(p => p.is_active)
        ?.length === 1
    ) {
      const _price = menuItem?.subscription_product?.prices;
      if (_price[0]?.is_custom_amount) {
        setRequestCustomAmount(_price[0]);
      } else {
        setSelectedPrice(_price);
        onSelection([_price[0]?.id], {});
      }
    }
  }, [menuItem?.id]);

  return (
    <>
      <p className="text-md tracking-wide uppercase font-semibold text-on-surface md:px-4 px-4 mb-0">
        Subscription Options
      </p>
      {menuItem.subscription_product?.offers_trial ? (
        <div className="flex flex-col px-4 py-2 mx-4 bg-tertiary-container rounded-md text-on-tertiary-container">
          <p className="text-md font-semibold">Free Trial Offered</p>
          <p className="text-md ">
            You can try any of the options below free for{' '}
            {menuItem.subscription_product?.trial_details?.days ?? 7} days.
          </p>
        </div>
      ) : (
        <></>
      )}
      <div className="rounded-md shadow-md mx-4 px-4 py-2 bg-surface-variant mt-2 mb-4">
        {menuItem.subscription_product?.prices
          ?.sort((a, b) => a.rank - b.rank)
          ?.filter(p => p.is_active)
          ?.map(p => {
            return (
              <div
                key={p.id?.toString()}
                onClick={() => {
                  if (p.is_custom_amount) {
                    setRequestCustomAmount(p);
                    return;
                  }
                  if (selectedPrice.filter(s => s.id === p.id).length !== 0) {
                    setSelectedPrice(selectedPrice.filter(s => s.id !== p.id));
                  } else if (
                    menuItem.subscription_product?.max_price_selection > 1
                  ) {
                    setSelectedPrice(selectedPrice.concat([p]));
                    onSelection(
                      selectedPrice.concat([p]).map(_p => _p.id),
                      {},
                    );
                  } else {
                    setSelectedPrice([p]);
                    onSelection([p.id], {});
                  }
                }}
                className={`flex flex-row items-start justify-between w-full px-4 py-2 bg-background rounded-md my-2 ${
                  selectedPrice.filter(s => s.id === p.id).length !== 0
                    ? 'border-primary'
                    : ' border-outline-variant '
                } border-2 `}
              >
                <div className="flex flex-col">
                  <div className="text-md font-semibold text-on-surface">
                    {p.name}
                  </div>
                  <p className="text-md text-on-surface">{p.description}</p>
                  <p className="text-md text-on-surface">
                    {p.is_custom_amount
                      ? 'Custom Amount'
                      : centsToDollars(p.amount)}{' '}
                    every {p.interval_count} {p.interval}s
                  </p>
                </div>
                <div>
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      className={'w-24 h-24 rounded-md object-cover'}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            );
          })}
      </div>
      {requestCustomAmount && (
        <CustomAmountInput
          min={requestCustomAmount.custom_amount_min}
          max={requestCustomAmount.custom_amount_max}
          onDone={v => {
            console.log(v);
            setSelectedPrice([requestCustomAmount]);
            setCustomPriceData({
              currency: 'usd',
              product: menuItem.subscription_product?.product_id,
              recurring: {
                interval: requestCustomAmount.interval,
                interval_count: requestCustomAmount.interval_count,
              },
              unit_amount: Math.round(v * 100),
            });
            onSelection([], {
              id: requestCustomAmount.id,
              currency: 'usd',
              product: menuItem.subscription_product?.product_id,
              recurring: {
                interval: requestCustomAmount.interval,
                interval_count: requestCustomAmount.interval_count,
              },
              unit_amount: Math.round(v * 100),
              name: requestCustomAmount.name,
            });
            setRequestCustomAmount(null);
          }}
          preSet={requestCustomAmount.custom_amount_preset}
        />
      )}
    </>
  );
}
